import type { IconName } from '@fortawesome/fontawesome-svg-core';
import type { SxProps } from '@mui/material';
import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import React from 'react';

import Icon from '@components/Icon/Icon';

import { shadeColor } from '@utils/shadeColor';

export interface RoundedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  autoSize?: boolean;
  height?: number | string;
  width?: number | string;
  disabled?: boolean;
  margin?: number;
  variant?: 'blue' | 'grey' | 'red' | 'white';
  iconName?: IconName;
  fontSize?: number;
  children?: React.ReactNode;
  squareShape?: boolean;
  textTransform?: string;
}

function resolveColors(
  variant: string,
  disabled: boolean,
  theme: Theme
): SxProps {
  const color = variant;
  const style = {
    color: '',
    text: '',
    hover: '',
    active: '',
    disabled: '',
    shadow: '',
  };
  switch (color) {
    case 'red':
      style.color = theme.palette.secondary.main;
      style.hover = shadeColor(theme.palette.secondary.main, 10);
      style.active = shadeColor(theme.palette.secondary.main, -10);
      style.disabled = theme.palette.secondary.main;
      style.text = theme.palette.secondary.contrastText;
      style.shadow = theme.shadows[1];
      break;
    case 'white':
      style.color = theme.palette.common.white;
      style.hover = theme.palette.common.accentLightblue;
      style.active = shadeColor(theme.palette.common.white, -10);
      style.disabled = 'transparent';
      style.text = theme.palette.primary.main;
      style.shadow = theme.shadows[1];
      break;
    case 'grey':
      style.color = theme.palette.grey.A200;
      style.hover = shadeColor(theme.palette.grey.A200, 10);
      style.active = shadeColor(theme.palette.grey.A200, -10);
      style.disabled = theme.palette.grey.A200;
      style.text = theme.palette.text.secondary;
      style.shadow = theme.shadows[1];
      break;
    case 'blue':
    default:
      style.color = theme.palette.primary.main;
      style.hover = theme.palette.primary.light;
      style.active = theme.palette.primary.dark;
      style.disabled = theme.palette.primary.main;
      style.text = theme.palette.primary.contrastText;
      style.shadow = theme.shadows[1];
      break;
  }

  return {
    color: style.text,
    backgroundColor:
      disabled && variant === 'white' ? style.disabled : style.color,
    boxShadow: disabled ? 'none' : style.shadow,
    '&:hover': {
      cursor: disabled ? 'auto' : 'pointer',
      backgroundColor: disabled ? style.disabled : style.hover,
      userSelect: 'none',
    },
    '&:focus': {
      cursor: disabled ? 'auto' : 'pointer',
      outline: 'none',
      border: `2px solid ${theme.palette.info.main}`,
    },
    '&:disabled': {
      border:
        disabled && variant === 'white'
          ? `1px solid ${theme.palette.divider}`
          : 'none',
    },
    '&:active': {
      backgroundColor: style.active,
    },
  };
}

function RoundedButton({
  variant = 'blue',
  autoSize = false,
  height = 40,
  onClick,
  width = '100%',
  disabled = false,
  margin = 10,
  iconName,
  children = null,
  fontSize,
  squareShape = false,
  textTransform = 'uppercase',
  ...props
}: RoundedButtonProps) {
  const theme = useTheme();

  const styleProps: SxProps = {
    fontSize: `${fontSize ?? 14}px`,
    height: typeof height === 'string' ? height : `${height}px`,
    minHeight: '32px',
    padding: autoSize ? '0 16px' : '0',
    margin: `${margin}px 0`,
    borderRadius: squareShape ? '5px' : '29px',
    width: autoSize ? 'auto' : typeof width === 'number' ? `${width}px` : width,
    transition: 'all 0.1s linear',
    ...props,
    ...resolveColors(variant, disabled, theme),
  };

  return (
    <Button
      data-testid="rounded-button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...(styleProps as any),
        typography: {
          fontWeight: iconName ? 600 : 500,
          ...(textTransform && {
            textTransform,
          }),
          fontSize: (styleProps as any)?.fontSize,
        },
      }}
      startIcon={
        iconName ? (
          <Icon clickable={!disabled} name={iconName} type="fas" size="sm" />
        ) : null
      }
      size="small"
      type={props.type || 'button'}
    >
      {children}
    </Button>
  );
}

export default RoundedButton;
